/* ============================================
   ADORN TAP - Theme JavaScript
   ============================================ */

(function() {
  'use strict';

  /* ---- Utility Functions ---- */
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  function debounce(fn, delay = 150) {
    let timer;
    return function(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    container.addEventListener('keydown', function(e) {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  /* ---- Header ---- */
  function initHeader() {
    const header = $('.site-header');
    if (!header) return;

    const announcementBar = $('.announcement-bar');
    let lastScroll = 0;
    let announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;

    function onScroll() {
      const scrollY = window.scrollY;

      if (scrollY > announcementHeight) {
        header.classList.add('site-header--scrolled');
        document.body.classList.add('announcement-hidden');
      } else {
        header.classList.remove('site-header--scrolled');
        document.body.classList.remove('announcement-hidden');
      }

      lastScroll = scrollY;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- Drawer Manager ---- */
  const drawerManager = {
    activeDrawer: null,
    closeAll() {
      const drawers = ['.cart-drawer', '.wishlist-drawer', '.mobile-menu', '.search-overlay', '.quick-view'];
      drawers.forEach(selector => {
        const drawer = $(selector);
        if (drawer && drawer.classList.contains('active')) {
          drawer.classList.remove('active');
        }
      });
      const overlays = ['.cart-drawer-overlay', '.wishlist-drawer-overlay', '.mobile-menu-overlay', '.search-overlay'];
      overlays.forEach(selector => {
        const overlay = $(selector);
        if (overlay && overlay.classList.contains('active')) {
          overlay.classList.remove('active');
        }
      });
      document.body.style.overflow = '';
      const menuToggle = $('.menu-toggle');
      if (menuToggle) menuToggle.classList.remove('active');
    },
    open(drawer, overlay) {
      this.closeAll();
      drawer.classList.add('active');
      if (overlay) overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.activeDrawer = drawer;
      trapFocus(drawer);
    },
    close(drawer, overlay) {
      drawer.classList.remove('active');
      if (overlay) overlay.classList.remove('active');
      document.body.style.overflow = '';
      this.activeDrawer = null;
    }
  };

  /* ---- Mobile Menu ---- */
  function initMobileMenu() {
    const toggles = $$('.menu-toggle');
    const menu = $('.mobile-menu');
    const close = $('.mobile-menu__close');
    const overlay = $('.mobile-menu-overlay');

    if (toggles.length === 0 || !menu) return;

    function openMenu() {
      drawerManager.open(menu, overlay);
      toggles.forEach(toggle => toggle.classList.add('active'));
    }

    function closeMenu() {
      drawerManager.close(menu, overlay);
      toggles.forEach(toggle => toggle.classList.remove('active'));
    }

    toggles.forEach(toggle => toggle.addEventListener('click', openMenu));
    if (close) close.addEventListener('click', closeMenu);
    if (overlay) overlay.addEventListener('click', closeMenu);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('active')) {
        closeMenu();
      }
    });
  }

  /* ---- Announcement Bar ---- */
  function initAnnouncementBar() {
    const bar = $('.announcement-bar');
    const closeBtn = $('.announcement-bar__close');
    if (!bar || !closeBtn) return;

    closeBtn.addEventListener('click', () => {
      bar.style.display = 'none';
      document.documentElement.style.setProperty('--announcement-height', '0px');
      sessionStorage.setItem('announcement-closed', 'true');
    });

    if (sessionStorage.getItem('announcement-closed') === 'true') {
      bar.style.display = 'none';
      document.documentElement.style.setProperty('--announcement-height', '0px');
    }
  }

  /* ---- Search Overlay ---- */
  function initSearch() {
    const searchBtn = $('.js-search-toggle');
    const overlay = $('.search-overlay');
    const closeBtn = $('.search-overlay__close');
    const input = $('.search-overlay__input');

    if (!overlay) return;

    function openSearch() {
      drawerManager.open(overlay);
      if (input) {
        setTimeout(() => input.focus(), 300);
      }
    }

    function closeSearch() {
      drawerManager.close(overlay);
    }

    if (searchBtn) searchBtn.addEventListener('click', openSearch);
    if (closeBtn) closeBtn.addEventListener('click', closeSearch);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('active')) {
        closeSearch();
      }
    });

    // Predictive Search
    if (input) {
      input.addEventListener('input', debounce(function() {
        const query = this.value.trim();
        if (query.length < 1) {
          const results = $('.search-overlay__results');
          if (results) results.innerHTML = '';
          return;
        }
        predictiveSearch(query);
      }, 200));
    }
  }

  async function predictiveSearch(query) {
    const resultsContainer = $('.search-overlay__results');
    if (!resultsContainer) return;

    try {
      // Fetch Products and Collections (Categories)
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=8&resources[options][unavailable_products]=last`;
      
      const response = await fetch(url);
      if (!response.ok) return;

      const data = await response.json();
      const products = data.resources.results.products || [];
      const collections = data.resources.results.collections || [];

      if (products.length === 0 && collections.length === 0) {
        resultsContainer.innerHTML = '<div style="text-align:center; padding: 40px;"><p style="color:#999;font-size:14px;">No matches found for "' + query + '"</p></div>';
        return;
      }

      let html = '<div class="predictive-search__container">';

      // Add Collections (Categories) first
      if (collections.length > 0) {
        html += '<div class="predictive-search__section"><h4 class="predictive-search__section-title">Categories</h4><ul class="predictive-search__results-list">';
        collections.forEach(item => {
          html += `
            <li class="predictive-search__list-item" role="option">
              <a href="${item.url}" class="predictive-search__item predictive-search__item--category">
                <span class="predictive-search__title">${item.title}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
              </a>
            </li>
          `;
        });
        html += '</ul></div>';
      }

      // Add Products
      if (products.length > 0) {
        html += '<div class="predictive-search__section"><h4 class="predictive-search__section-title">Products</h4><ul class="predictive-search__results-list">';
        products.forEach(item => {
          const price = (item.price / 100).toLocaleString('en-IN', { style: 'currency', currency: 'INR' }).replace('₹', 'Rs. ');
          html += `
            <li class="predictive-search__list-item" role="option">
              <a href="${item.url}" class="predictive-search__item">
                <img class="predictive-search__image" src="${item.image}" alt="${item.title}" loading="lazy">
                <div class="predictive-search__info">
                  <h3 class="predictive-search__title">${item.title}</h3>
                  <span class="predictive-search__price">${price}</span>
                </div>
              </a>
            </li>
          `;
        });
        html += '</ul></div>';
      }

      html += '</div>';
      resultsContainer.innerHTML = html;
    } catch (error) {
      console.error('Predictive search error:', error);
      resultsContainer.innerHTML = '';
    }
  }

  /* ---- Cart Drawer ---- */
  function initCartDrawer() {
    const cartBtn = $$('.js-cart-toggle');
    const drawer = $('.cart-drawer');
    const closeBtn = $('.cart-drawer__close');
    const overlay = $('.cart-drawer-overlay');

    if (!drawer) return;

    function openCart() {
      drawerManager.open(drawer, overlay);
    }

    function closeCart() {
      drawerManager.close(drawer, overlay);
    }

    cartBtn.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (drawer.classList.contains('active')) {
        closeCart();
      } else {
        openCart();
      }
    }));

    if (closeBtn) closeBtn.addEventListener('click', closeCart);
    if (overlay) overlay.addEventListener('click', closeCart);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('active')) {
        closeCart();
      }
    });

    // Note: refreshCartDrawer is called directly in updateCartItemByKey and initAddToCart.
    // No need for a cart:updated event listener here — it would cause double refreshes.

    // Handle quantity changes and remove in cart drawer
    drawer.addEventListener('click', async function(e) {
      const qtyBtn = e.target.closest('.cart-item__qty-btn');
      const removeBtn = e.target.closest('.cart-item__remove');
      
      if (!qtyBtn && !removeBtn) return;
      
      e.preventDefault();
      
      if (qtyBtn) {
        const itemKey = qtyBtn.dataset.key;
        const action = qtyBtn.dataset.action;
        const qtyValue = drawer.querySelector(`.cart-item__qty-value[data-key="${itemKey}"]`);
        
        if (!qtyValue) return;
        
        let currentQty = parseInt(qtyValue.textContent);
        let newQty = action === 'increase' ? currentQty + 1 : currentQty - 1;
        
        if (newQty < 1) {
          await updateCartItemByKey(itemKey, 0);
          return;
        }
        
        // Disable button during update
        qtyBtn.disabled = true;
        
        // Clear previous warnings
        const cartItem = qtyBtn.closest('.cart-item');
        const existingWarning = cartItem ? cartItem.querySelector('.cart-item__stock-warning') : null;
        if (existingWarning) existingWarning.style.display = 'none';
        
        try {
          const data = await updateCartItemByKey(itemKey, newQty);
          
          // Check if Shopify capped the quantity (stock limit reached)
          if (data && data.items) {
            const updatedItem = data.items.find(item => item.key === itemKey);
            if (updatedItem && updatedItem.quantity < newQty) {
              // Stock limit was hit — show warning
              showCartStockWarning(itemKey, updatedItem.quantity);
            }
          }
        } catch (error) {
          console.error('Quantity update failed:', error);
          qtyBtn.disabled = false;
        }
      } else if (removeBtn) {
        const itemKey = removeBtn.dataset.key;
        try {
          await updateCartItemByKey(itemKey, 0);
        } catch (error) {
          console.error('Remove item failed:', error);
        }
      }
    });
  }
  
  function showCartStockWarning(itemKey, maxQty) {
    const drawer = $('.cart-drawer');
    if (!drawer) return;
    const cartItem = drawer.querySelector(`.cart-item[data-key="${itemKey}"]`);
    if (!cartItem) return;
    
    let warning = cartItem.querySelector('.cart-item__stock-warning');
    if (!warning) {
      warning = document.createElement('p');
      warning.className = 'cart-item__stock-warning';
      warning.style.cssText = 'color: #c00; font-size: 10px; margin-top: 4px; font-weight: 500; animation: fadeIn 0.3s ease;';
      const details = cartItem.querySelector('.cart-item__details');
      if (details) details.appendChild(warning);
    }
    warning.textContent = 'Only ' + maxQty + ' left in stock';
    warning.style.display = 'block';
    
    // Auto-hide after 4 seconds
    setTimeout(() => { if (warning) warning.style.display = 'none'; }, 4000);
  }

  async function refreshCartDrawer(cartData) {
    try {
      const cart = cartData || await (await fetch('/cart.js')).json();
      
      // Update cart items container
      const container = $('.cart-drawer__items');
      if (container) {
        if (cart.item_count === 0) {
          container.innerHTML = `
            <div style="text-align: center; padding: var(--space-4xl) var(--space-xl);">
              <p style="color: var(--color-text-light); margin-bottom: var(--space-xl);">Your bag is empty.</p>
              <a href="/collections/all" class="btn btn--primary btn--sm">Continue Shopping</a>
            </div>
          `;
          
          // Remove footer if empty
          const footer = $('.cart-drawer__footer');
          if (footer) footer.remove();
        } else {
          // Rebuild cart items HTML using variant_id for reliable updates
          container.innerHTML = cart.items.map((item) => {
            return `
              <div class="cart-item" data-key="${item.key}" style="grid-template-columns: 80px 1fr; padding: var(--space-md) 0;">
                <a href="${item.url}" class="cart-item__image" style="aspect-ratio: 3/4;">
                  ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
                </a>
                <div class="cart-item__details">
                  <a href="${item.url}" class="cart-item__title" style="font-size: var(--font-size-sm);">${item.product_title}</a>
                  ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant" style="font-size: var(--font-size-xs);">${item.variant_title}</p>` : ''}
                  <p class="cart-item__price" style="font-size: var(--font-size-sm);" data-price-key="${item.key}">${formatMoney(item.final_line_price)}</p>
                  <div style="display: flex; align-items: center; gap: var(--space-md); margin-top: var(--space-sm);">
                    <div class="cart-item__qty" style="display: flex; align-items: center; gap: 4px;">
                      <button class="cart-item__qty-btn" data-key="${item.key}" data-action="decrease" aria-label="Decrease quantity">−</button>
                      <span class="cart-item__qty-value" data-key="${item.key}">${item.quantity}</span>
                      <button class="cart-item__qty-btn" data-key="${item.key}" data-action="increase" aria-label="Increase quantity">+</button>
                    </div>
                    <button class="cart-item__remove" data-key="${item.key}" style="font-size: 0.625rem;">Remove</button>
                  </div>
                </div>
              </div>
            `;
          }).join('');
          
          // Update or create footer
          updateCartFooter(cart);
        }
      }
      
      // Update cart title
      const title = $('.cart-drawer__title');
      if (title) {
        title.textContent = `Your Bag (${cart.item_count})`;
      }
      
      updateCartCount();
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
  
  function updateCartFooter(cart) {
    let footer = $('.cart-drawer__footer');
    
    if (!footer && cart.item_count > 0) {
      // Create footer if it doesn't exist
      const drawer = $('.cart-drawer');
      if (!drawer) return;
      
      footer = document.createElement('div');
      footer.className = 'cart-drawer__footer';
      drawer.appendChild(footer);
    } else if (cart.item_count === 0 && footer) {
      footer.remove();
      return;
    }
    
    if (footer && cart.item_count > 0) {
      footer.innerHTML = `
        <div class="cart-drawer__total">
          <span>Subtotal</span>
          <span>${formatMoney(cart.total_price)}</span>
        </div>
        <p style="font-size: var(--font-size-xs); color: var(--color-text-light); margin-bottom: var(--space-md); text-align: center;">
          Shipping & taxes calculated at checkout
        </p>
        <a href="/checkout" class="btn btn--primary btn--full">Checkout</a>
      `;
    }
  }

  // Update cart item quantity using variant_id (more reliable than line number)
  async function updateCartItemByKey(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const data = await response.json();
      
      // Refresh cart drawer with new data instantly (skips a 2nd fetch)
      await refreshCartDrawer(data);
      
      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      
      return data;
    } catch (error) {
      console.error('Update cart item error:', error);
      throw error;
    }
  }

  // Legacy function kept for compatibility
  async function updateCartItem(line, quantity) {
    // Find variant_id from line number by fetching cart
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const item = cart.items[line - 1];
      if (item) {
        return updateCartItemByKey(item.key, quantity);
      }
    } catch (error) {
      console.error('Update cart item error:', error);
      throw error;
    }
  }

  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const counts = $$('.cart-count');
      counts.forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /* ---- Add to Cart ---- */
  function initAddToCart() {
    // Intercept all cart/add form submissions via AJAX
    document.addEventListener('submit', async function(e) {
      const form = e.target.closest('form[action="/cart/add"], form[action*="/cart/add"], form[data-product-form]');
      if (!form) return;

      // Skip the Quick Add Popup form — it has its own handler in initQuickAddPopup
      if (form.id === 'quickAddForm') return;

      e.preventDefault();
      e.stopPropagation();

      // Detect if this is a Buy Now form (from product card)
      const isBuyNow = form.classList.contains('js-product-form-buy') || 
                        form.querySelector('.product-card__buy-btn');

      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = isBuyNow ? 'Processing...' : 'Adding...';
      }

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        const data = await response.json();

        // Dispatch event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

        if (isBuyNow) {
          // Buy Now: redirect to checkout immediately
          window.location.href = '/checkout';
          return;
        }

        // Regular Add to Cart: stay on current page, open cart drawer
        await refreshCartDrawer();
        updateCartCount();

        const drawer = $('.cart-drawer');
        const overlay = $('.cart-drawer-overlay');
        if (drawer) {
          drawerManager.open(drawer, overlay);
        }

        if (submitBtn) {
          submitBtn.textContent = 'Added!';
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 1500);
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        if (submitBtn) {
          submitBtn.textContent = 'Error - Try Again';
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 2000);
        }
      }
    });

    // Safety: prevent any native form submission clicks from navigating away
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('form[action="/cart/add"] [type="submit"], form[action*="/cart/add"] [type="submit"]');
      if (!btn) return;
      const form = btn.closest('form');
      if (form && !form.dataset.allowNative) {
        // The submit event handler above will handle this via AJAX
        // This just prevents any edge case double-submission
        return;
      }
    });
  }

  /* ---- Quick Add Form (Collection Page) ---- */
  function initQuickAddForm() {
    document.querySelectorAll('.quick-add-form').forEach(form => {
      const addBtn = form.querySelector('.quick-add-btn');
      if (!addBtn) return;
      
      const variants = JSON.parse(form.dataset.variants || '[]');
      let selectedOptions = {};
      
      // Variant option selection (all quick-variant-btn buttons)
      form.querySelectorAll('.quick-variant-btn').forEach(btn => {
        // Auto-select first option in each group
        const optionName = btn.dataset.option;
        if (!selectedOptions[optionName]) {
          selectedOptions[optionName] = btn.dataset.value;
          btn.classList.add('active');
        }
        
        btn.addEventListener('click', function() {
          const optName = this.dataset.option;
          const value = this.dataset.value;
          
          selectedOptions[optName] = value;
          
          // Remove active from siblings with same option name
          form.querySelectorAll(`.quick-variant-btn[data-option="${optName}"]`).forEach(b => b.classList.remove('active'));
          this.classList.add('active');
          
          updateQuickAddButton();
        });
      });
      
      function updateQuickAddButton() {
        // Check if we have a valid variant with selected options
        const matchingVariant = variants.find(v => {
          return v.options.every((opt, idx) => {
            const optionName = Object.keys(selectedOptions)[idx];
            return !optionName || v.options[idx] === selectedOptions[optionName];
          });
        });
        
        if (matchingVariant && matchingVariant.available) {
          addBtn.disabled = false;
        } else if (!matchingVariant) {
          addBtn.disabled = false;
        } else {
          addBtn.disabled = true;
        }
      }
      
      // Initialize button state
      updateQuickAddButton();
      
      // Add to cart
      addBtn.addEventListener('click', async function() {
        const originalText = this.textContent;
        
        // Find variant matching selected options
        let variantToAdd = null;
        
        variantToAdd = variants.find(v => {
          return v.options.every((opt, idx) => {
            const optionName = Object.keys(selectedOptions)[idx];
            return !optionName || v.options[idx] === selectedOptions[optionName];
          });
        });
        
        if (!variantToAdd || !variantToAdd.available) {
          this.textContent = 'Unavailable';
          setTimeout(() => {
            this.textContent = originalText;
          }, 1500);
          return;
        }
        
        this.disabled = true;
        this.textContent = 'Adding...';
        
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantToAdd.id, quantity: 1 })
          });
          
          if (!response.ok) throw new Error('Failed to add');
          
          this.textContent = 'Added!';
          
          // Update cart
          updateCartCount();
          await refreshCartDrawer();
          
          // Open cart drawer
          const drawer = $('.cart-drawer');
          const overlay = $('.cart-drawer-overlay');
          if (drawer) {
            drawerManager.open(drawer, overlay);
          }
          
          setTimeout(() => {
            this.textContent = originalText;
            this.disabled = false;
          }, 1500);
          
        } catch (error) {
          console.error('Quick add error:', error);
          this.textContent = 'Error';
          setTimeout(() => {
            this.textContent = originalText;
            this.disabled = false;
          }, 2000);
        }
      });
      
      // Initialize button state
      updateQuickAddButton();
    });
  }

  /* ---- Quick View ---- */
  function initQuickView() {
    document.addEventListener('click', async function(e) {
      const btn = e.target.closest('.js-quick-view');
      if (!btn) return;

      e.preventDefault();
      const handle = btn.dataset.handle;
      if (!handle) return;

      const modal = $('.quick-view');
      if (!modal) return;

      try {
        const response = await fetch(`/products/${handle}?section_id=quick-view-content`);
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const content = doc.querySelector('.quick-view-content');

        const container = modal.querySelector('.quick-view__content');
        if (container && content) {
          container.innerHTML = content.innerHTML;
        }

        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
      } catch (error) {
        console.error('Quick view error:', error);
        window.location.href = `/products/${handle}`;
      }
    });

    // Close quick view
    document.addEventListener('click', function(e) {
      if (e.target.closest('.quick-view__close') || e.target.closest('.quick-view__overlay')) {
        const modal = $('.quick-view');
        if (modal) {
          modal.classList.remove('active');
          document.body.style.overflow = '';
        }
      }
    });
  }

  /* ---- Product Page ---- */
  function initProductPage() {
    // Initialize first available variant
    initFirstAvailableVariant();
    
    // Native Scroll Gallery
    const galleryMain = $('.product-gallery__main');
    const slides = $$('.product-gallery__slide');
    const navPrev = $('.product-gallery__nav--prev');
    const navNext = $('.product-gallery__nav--next');
    
    // Navigation buttons for native scroll
    if (slides.length > 1 && galleryMain) {
      if (navPrev) {
        navPrev.addEventListener('click', function() {
          galleryMain.scrollBy({ left: -galleryMain.clientWidth, behavior: 'smooth' });
        });
      }
      
      if (navNext) {
        navNext.addEventListener('click', function() {
          galleryMain.scrollBy({ left: galleryMain.clientWidth, behavior: 'smooth' });
        });
      }

      // Keyboard navigation
      document.addEventListener('keydown', function(e) {
        if (!galleryMain) return;
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        
        if (galleryMain.closest('.product-page') && slides.length > 1) {
          if (e.key === 'ArrowLeft') {
            galleryMain.scrollBy({ left: -galleryMain.clientWidth, behavior: 'smooth' });
          } else if (e.key === 'ArrowRight') {
            galleryMain.scrollBy({ left: galleryMain.clientWidth, behavior: 'smooth' });
          }
        }
      });
    }

    // Variant selector - handle both size and color options
    const variantSelectors = $$('[data-variant]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('click', function() {
        const parent = this.closest('.product-option');
        if (!parent) return;
        
        // Remove active from all options in this group
        parent.querySelectorAll('[data-variant]').forEach(v => v.classList.remove('active'));
        this.classList.add('active');
        
        // Update the selected label
        const labelSpan = parent.querySelector('.product-option__label span');
        if (labelSpan) {
          labelSpan.textContent = this.dataset.optionValue || this.dataset.value;
        }
        
        updateVariant();
      });
    });

    // Quantity selector with stock limit checking
    const qtyBtns = $$('.quantity-selector__btn');
    qtyBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const input = this.closest('.quantity-selector').querySelector('.quantity-selector__input');
        if (!input) return;
        let value = parseInt(input.value) || 1;
        
        if (this.dataset.action === 'decrease') {
          value = Math.max(1, value - 1);
          // Clear warning on decrease
          const warning = document.getElementById('qtyStockWarning');
          if (warning) warning.style.display = 'none';
        } else {
          // Check stock limit before increasing
          const productData = window.productData;
          if (productData) {
            const variantInput = $('[name="id"]');
            const currentVariantId = variantInput ? variantInput.value : null;
            const currentVariant = currentVariantId ? 
              productData.variants.find(v => v.id == currentVariantId) : null;
            
            if (currentVariant && currentVariant.inventory_quantity !== undefined && currentVariant.inventory_quantity !== null) {
              const maxQty = currentVariant.inventory_quantity;
              if (value + 1 > maxQty && currentVariant.inventory_management) {
                // Show stock warning
                const warning = document.getElementById('qtyStockWarning');
                if (warning) {
                  warning.textContent = 'Only ' + maxQty + ' left in stock';
                  warning.style.display = 'block';
                }
                return; // Don't increase beyond stock
              }
            }
          }
          value++;
          // Clear warning if within stock
          const warning = document.getElementById('qtyStockWarning');
          if (warning) warning.style.display = 'none';
        }
        input.value = value;
      });
    });

    // Product tabs / accordion
    const tabs = $$('.product-tab__header');
    tabs.forEach(tab => {
      tab.addEventListener('click', function() {
        const parent = this.closest('.product-tab');
        if (!parent) return;
        parent.classList.toggle('active');
      });
    });

    // Buy Now button
    const buyNowBtn = $('[data-buy-now]');
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', function() {
        const form = this.closest('form');
        if (!form) return;
        
        const variantId = form.querySelector('[name="id"]')?.value;
        const quantity = form.querySelector('[name="quantity"]')?.value || 1;
        
        if (variantId) {
          // Add to cart and redirect to checkout
          fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: parseInt(quantity) })
          })
          .then(response => response.json())
          .then(() => {
            window.location.href = '/checkout';
          })
          .catch(error => {
            console.error('Buy Now error:', error);
          });
        }
      });
    }
  }

  function updateVariant() {
    const selectedOptions = {};
    const optionContainers = $$('.product-option');
    
    // Get currently selected options by name
    optionContainers.forEach((optionGroup) => {
      const optionName = optionGroup.dataset.optionName;
      const active = optionGroup.querySelector('.product-option__value.active, .color-option-btn.active, [data-variant].active');
      if (active && active.dataset.optionValue) {
        selectedOptions[optionName] = active.dataset.optionValue;
      }
    });

    const variantInput = $('[name="id"]');
    const productData = window.productData;
    if (!productData || !variantInput) return;

    // Find matching variant by checking all options
    const matchingVariant = productData.variants.find(variant => {
      return variant.options.every((opt, idx) => {
        const optionName = productData.options[idx]?.toLowerCase().replace(/\s+/g, '_');
        const optionNameAlt = productData.options[idx];
        return selectedOptions[optionName] === opt || selectedOptions[optionNameAlt] === opt;
      });
    });

    // Update all option availability based on current selections
    optionContainers.forEach((optionGroup) => {
      const optionButtons = optionGroup.querySelectorAll('[data-variant]');
      const optionName = optionGroup.dataset.optionName;
      const optionIndex = parseInt(optionGroup.dataset.optionIndex) || 0;
      
      optionButtons.forEach(btn => {
        const value = btn.dataset.optionValue;
        
        // Check availability by temporarily adding this option
        const testOptions = { ...selectedOptions };
        testOptions[optionName] = value;
        
        const hasAvailableVariant = productData.variants.some(variant => {
          return variant.available && variant.options.every((opt, idx) => {
            const name = productData.options[idx];
            const testOpt = testOptions[name];
            return !testOpt || variant.options[idx] === testOpt;
          });
        });
        
        // Update button state
        btn.classList.remove('unavailable', 'product-option__value--soldout', 'color-option-btn--unavailable');
        btn.disabled = false;
        
        if (!hasAvailableVariant) {
          btn.classList.add('unavailable', 'product-option__value--soldout', 'color-option-btn--unavailable');
          btn.disabled = true;
        }
      });
    });

    if (matchingVariant) {
      variantInput.value = matchingVariant.id;

      // Update availability indicator
      const availabilityEl = $('.product-availability');
      if (availabilityEl) {
        if (matchingVariant.available) {
          availabilityEl.innerHTML = '<span class="availability available">&#10004; In Stock</span>';
          availabilityEl.style.display = '';
        } else {
          availabilityEl.innerHTML = '<span class="availability unavailable">&#10006; Out of Stock</span>';
          availabilityEl.style.display = '';
        }
      }

      // Update price
      const priceContainer = $('#productPrice');
      if (priceContainer) {
        const currentPrice = priceContainer.querySelector('.product-info__price--current');
        const comparePrice = priceContainer.querySelector('.product-info__price--compare');
        
        if (currentPrice) {
          currentPrice.textContent = formatMoney(matchingVariant.price);
        }
        if (comparePrice) {
          if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
            comparePrice.textContent = formatMoney(matchingVariant.compare_at_price);
            comparePrice.style.display = '';
            if (currentPrice) currentPrice.classList.add('product-info__price--sale');
          } else {
            comparePrice.style.display = 'none';
            if (currentPrice) currentPrice.classList.remove('product-info__price--sale');
          }
        }
      }

      // Update button states based on availability
      const submitBtn = $('[type="submit"][name="add"]');
      const buyNowBtn = $('[data-buy-now]');
      
      [submitBtn, buyNowBtn].forEach(btn => {
        if (btn) {
          if (matchingVariant.available) {
            btn.disabled = false;
            btn.textContent = btn.dataset.originalText || 'Add to Cart';
          } else {
            btn.disabled = true;
            btn.textContent = 'Sold Out';
          }
        }
      });

      // Update URL
      const url = new URL(window.location);
      url.searchParams.set('variant', matchingVariant.id);
      window.history.replaceState({}, '', url);

      // Update gallery - scroll to variant image
      const galleryMain = $('.product-gallery__main');
      const slides = $$('.product-gallery__slide');
      
      // Get variant image URL from Shopify JSON
      let imageUrl = null;
      let imageIndex = 0;
      
      if (matchingVariant.featured_image) {
        // Variant has an assigned image - use it directly
        if (typeof matchingVariant.featured_image === 'string') {
          imageUrl = matchingVariant.featured_image;
        } else if (matchingVariant.featured_image.src) {
          imageUrl = matchingVariant.featured_image.src;
        } else if (matchingVariant.featured_image.id) {
          // Find the index of this image in the gallery
          imageIndex = productData.images.findIndex(img => 
            img.id === matchingVariant.featured_image.id ||
            (img.src && img.src.includes(matchingVariant.featured_image.id.toString()))
          );
          if (imageIndex === -1) imageIndex = 0;
        }
      }
      
      // Scroll to the correct slide
      if (galleryMain && slides.length > 0) {
        const targetSlide = slides[imageIndex] || slides[0];
        if (targetSlide) {
          targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        }
      }
    }
  }
  
  // Initialize first available variant on page load
  function initFirstAvailableVariant() {
    const productData = window.productData;
    if (!productData || !productData.variants) return;
    
    // Find first available variant
    const firstAvailable = productData.variants.find(v => v.available);
    const firstVariant = productData.variants[0];
    const variantToSelect = firstAvailable || firstVariant;
    
    if (!variantToSelect) return;
    
    // Set the hidden select
    const variantInput = $('[name="id"]');
    if (variantInput) {
      variantInput.value = variantToSelect.id;
    }
    
    // Update option buttons to match
    const optionContainers = $$('.product-option');
    variantToSelect.options.forEach((optValue, idx) => {
      if (optionContainers[idx]) {
        const btn = optionContainers[idx].querySelector(`[data-option-value="${optValue}"]`);
        if (btn) {
          optionContainers[idx].querySelectorAll('[data-variant]').forEach(v => v.classList.remove('active'));
          btn.classList.add('active');
          
          const labelSpan = optionContainers[idx].querySelector('.product-option__label span');
          if (labelSpan) {
            labelSpan.textContent = optValue;
          }
        }
      }
    });
    
    // Update availability indicator
    const availabilityEl = $('.product-availability');
    if (availabilityEl) {
      if (variantToSelect.available) {
        availabilityEl.innerHTML = '<span class="availability available">&#10004; In Stock</span>';
      } else {
        availabilityEl.innerHTML = '<span class="availability unavailable">&#10006; Out of Stock</span>';
      }
      availabilityEl.style.display = '';
    }
    
    // Run updateVariant to sync everything
    setTimeout(updateVariant, 50);
  }

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return window.Shopify && window.Shopify.currency
      ? `${window.Shopify.currency.active} ${amount}`
      : `Rs. ${amount}`;
  }

  /* ---- FAQ Accordion ---- */
  function initFAQ() {
    const questions = $$('.faq-item__question');
    questions.forEach(question => {
      question.addEventListener('click', function() {
        const item = this.closest('.faq-item');
        if (!item) return;

        // Close others
        $$('.faq-item').forEach(faq => {
          if (faq !== item) faq.classList.remove('active');
        });

        item.classList.toggle('active');
      });
    });
  }

  /* ---- Scroll Animations ---- */
  function initScrollAnimations() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    $$('.fade-in, .slide-in-left, .fade-in-up').forEach(el => {
      observer.observe(el);
    });
  }

  /* ---- Lazy Loading ---- */
  function initLazyLoad() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              img.removeAttribute('data-srcset');
            }
            img.classList.remove('lazyload');
            img.classList.add('lazyloaded');
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '200px'
      });

      $$('img.lazyload').forEach(img => imageObserver.observe(img));
    }
  }

  /* ---- Collection Page ---- */
  function initCollectionPage() {
    // Sort by
    const sortSelect = $('.collection-page__sort select');
    if (sortSelect) {
      sortSelect.addEventListener('change', function() {
        const url = new URL(window.location);
        url.searchParams.set('sort_by', this.value);
        window.location.href = url.toString();
      });
    }

    // Layout toggle
    const layoutBtns = $$('.collection-page__layout-btn');
    const productGrid = $('.collection-page__products');
    layoutBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        layoutBtns.forEach(b => b.classList.remove('active'));
        this.classList.add('active');
        const cols = this.dataset.cols;
        if (productGrid && cols) {
          productGrid.className = productGrid.className.replace(/grid--\d/g, '');
          productGrid.classList.add(`grid--${cols}`);
        }
      });
    });

    // Filter toggles
    const filterToggles = $$('.collection-filter__title');
    filterToggles.forEach(toggle => {
      toggle.addEventListener('click', function() {
        this.closest('.collection-filter').classList.toggle('active');
      });
    });
  }

  /* ---- Newsletter Form ---- */
  function initNewsletter() {
    const forms = $$('.newsletter__form, .site-footer__newsletter-form');
    forms.forEach(form => {
      form.addEventListener('submit', function(e) {
        // Let Shopify handle the form submission
        const input = this.querySelector('input[type="email"]');
        if (input && !input.value.trim()) {
          e.preventDefault();
          input.focus();
        }
      });
    });
  }

  /* ---- Cart Page ---- */
  function initCartPage() {
    // Cart item quantity removal
    document.addEventListener('click', async function(e) {
      const removeBtn = e.target.closest('.cart-item__remove');
      if (!removeBtn) return;

      // Skip if this is inside the cart drawer (handled by initCartDrawer)
      if (removeBtn.closest('.cart-drawer')) return;

      e.preventDefault();
      const line = removeBtn.dataset.line;
      if (!line) return;

      try {
        const response = await fetch(window.routes ? window.routes.cart_change_url : '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line), quantity: 0 })
        });

        if (response.ok) {
          window.location.reload(); // Reload to refresh all Liquid elements and Oracle Widget
        } else {
          console.error('Failed to remove item from cart');
        }
      } catch (error) {
        console.error('Error removing cart item:', error);
      }
    });

    // Cart page quantity buttons clicks
    document.addEventListener('click', async function(e) {
      const qtyBtn = e.target.closest('.js-cart-qty');
      if (!qtyBtn) return;

      e.preventDefault();
      const line = qtyBtn.dataset.line;
      const action = qtyBtn.dataset.action;
      const input = qtyBtn.parentElement.querySelector('.quantity-selector__input');
      if (!input || !line) return;

      let currentQty = parseInt(input.value) || 1;
      let newQty = action === 'increase' ? currentQty + 1 : currentQty - 1;
      if (newQty < 1) newQty = 0;

      try {
        qtyBtn.disabled = true;
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line), quantity: newQty })
        });

        if (response.ok) {
          window.location.reload(); // Reload to refresh all Liquid elements and Oracle Widget
        } else {
          qtyBtn.disabled = false;
        }
      } catch (error) {
        console.error('Error changing cart page quantity:', error);
        qtyBtn.disabled = false;
      }
    });
  }

  /* ---- Smooth Image Parallax ---- */
  function initParallax() {
    const parallaxElements = $$('[data-parallax]');
    if (!parallaxElements.length) return;

    function onScroll() {
      const scrollY = window.scrollY;
      parallaxElements.forEach(el => {
        const speed = parseFloat(el.dataset.parallax) || 0.3;
        const rect = el.getBoundingClientRect();
        const offset = (rect.top + scrollY - window.innerHeight / 2) * speed;
        el.style.transform = `translateY(${offset}px)`;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---- Cookie Consent ---- */
  function initCookieConsent() {
    const banner = $('.cookie-consent');
    if (!banner) return;

    if (localStorage.getItem('cookie-consent') === 'accepted') {
      banner.style.display = 'none';
      return;
    }

    banner.style.display = 'block';

    const acceptBtn = banner.querySelector('.cookie-consent__accept');
    if (acceptBtn) {
      acceptBtn.addEventListener('click', () => {
        localStorage.setItem('cookie-consent', 'accepted');
        banner.style.display = 'none';
      });
    }
  }

  /* ---- Wishlist ---- */
  function initWishlist() {
    console.log('Initializing wishlist...');
    
    // Wishlist drawer toggle
    const wishlistBtn = $$('.js-wishlist-toggle');
    const drawer = $('.wishlist-drawer');
    const closeBtn = $('.wishlist-drawer__close');
    const overlay = $('.wishlist-drawer-overlay');

    if (drawer) {
      function openWishlist() {
        console.log('Opening wishlist drawer...');
        drawerManager.open(drawer, overlay);
        // Refresh drawer content every time it's opened
        refreshWishlistDrawer();
      }

      function closeWishlist() {
        drawerManager.close(drawer, overlay);
      }

      wishlistBtn.forEach(btn => btn.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Wishlist icon clicked, opening drawer...');
        if (drawer.classList.contains('active')) {
          closeWishlist();
        } else {
          openWishlist();
        }
      }));

      if (closeBtn) closeBtn.addEventListener('click', closeWishlist);
      if (overlay) overlay.addEventListener('click', closeWishlist);

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && drawer.classList.contains('active')) {
          closeWishlist();
        }
      });
    }

    // Add all to cart button
    const addAllBtn = $('.js-add-all-to-cart');
    if (addAllBtn) {
      addAllBtn.addEventListener('click', async function() {
        const wishlist = JSON.parse(localStorage.getItem('theme_wishlist') || '[]');
        
        for (const item of wishlist) {
          const variantId = typeof item === 'object' ? item.variantId : null;
          const handle = typeof item === 'object' ? item.handle : item;
          
          try {
            const idToUse = variantId;
            if (!idToUse) {
              const response = await fetch(`/products/${handle}.js`);
              if (!response.ok) continue;
              const product = await response.json();
              if (product.variants && product.variants.length > 0) {
                await fetch('/cart/add.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: product.variants[0].id, quantity: 1 })
                });
              }
            } else {
              await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: idToUse, quantity: 1 })
              });
            }
          } catch (error) {
            console.error('Error adding to cart:', error);
          }
        }
        
        // Open cart drawer
        const cartDrawer = $('.cart-drawer');
        const cartOverlay = $('.cart-drawer-overlay');
        if (cartDrawer) {
          drawerManager.open(cartDrawer, cartOverlay);
          refreshCartDrawer();
        }
        
        // Clear wishlist
        localStorage.setItem('theme_wishlist', JSON.stringify([]));
        refreshWishlistDrawer();
        updateWishlistCount();
      });
    }
    
    window.addToWishlist = function(e, forcedHandle, forcedVariantId) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      const wishlistBtn = e?.target?.closest('.product-card__wishlist, .product-info__wishlist-btn') || 
                          (forcedHandle ? document.querySelector(`[data-product-handle="${forcedHandle}"]`) : null);
      
      if (!wishlistBtn && !forcedHandle) return;

      let productHandle = forcedHandle || wishlistBtn.dataset.productHandle;
      let variantId = forcedVariantId || wishlistBtn.dataset.variantId || null;
      
      // Fallback: try to get from product card
      if (!productHandle && wishlistBtn) {
        const productCard = wishlistBtn.closest('.product-card');
        const productLink = productCard?.querySelector('a[href*="/products/"]');
        if (productLink) {
          const url = productLink.getAttribute('href');
          productHandle = url.replace('/products/', '').split('?')[0];
        }
      }

      if (!productHandle) {
        console.error('Could not find product handle for wishlist');
        return;
      }

      console.log('Toggling wishlist for:', productHandle, 'Variant:', variantId);

      let wishlist = JSON.parse(localStorage.getItem('theme_wishlist') || '[]');
      
      // Migration: convert old string handles to objects
      wishlist = wishlist.map(item => typeof item === 'string' ? { handle: item, variantId: null } : item);

      const index = wishlist.findIndex(item => item.handle === productHandle && (variantId ? item.variantId == variantId : true));

      if (index > -1) {
        // Remove from wishlist
        wishlist.splice(index, 1);
        if (wishlistBtn) wishlistBtn.classList.remove('active');
        console.log('✓ Removed from wishlist:', productHandle);
      } else {
        // Add to wishlist
        wishlist.push({ handle: productHandle, variantId: variantId });
        if (wishlistBtn) wishlistBtn.classList.add('active');
        console.log('✓ Added to wishlist:', productHandle, 'Variant:', variantId);
      }

      localStorage.setItem('theme_wishlist', JSON.stringify(wishlist));
      localStorage.setItem('adorn_wishlist', JSON.stringify(wishlist.map(i => i.handle))); // Sync for backward compatibility
      
      // Update icon
      if (wishlistBtn) {
        const svg = wishlistBtn.querySelector('svg');
        if (svg) {
          const isActive = wishlist.some(item => item.handle === productHandle);
          if (isActive) {
            svg.setAttribute('fill', 'currentColor');
          } else {
            svg.setAttribute('fill', 'none');
          }
        }
      }
      
      // Update wishlist count and drawer
      if (typeof updateWishlistCount === 'function') updateWishlistCount();
      if (typeof refreshWishlistDrawer === 'function') refreshWishlistDrawer();
    };

    document.addEventListener('click', function(e) {
      const wishlistBtn = e.target.closest('.product-card__wishlist, .product-info__wishlist-btn');
      if (wishlistBtn) {
        window.addToWishlist(e);
      }
    });

    // Load wishlist state on page load
    const wishlist = JSON.parse(localStorage.getItem('theme_wishlist') || '[]');
    console.log('Loading wishlist state:', wishlist.length, 'items');
    
    $$('.product-card__wishlist, .product-info__wishlist-btn').forEach(btn => {
      let productHandle = btn.dataset.productHandle;
      
      if (!productHandle) {
        const productCard = btn.closest('.product-card');
        const productLink = productCard?.querySelector('a[href*="/products/"]');
        if (productLink) {
          const url = productLink.getAttribute('href');
          productHandle = url.replace('/products/', '').split('?')[0];
        }
      }

      if (!productHandle && window.location.pathname.includes('/products/')) {
        productHandle = window.location.pathname.replace('/products/', '').split('?')[0];
      }

      const isActive = wishlist.some(item => (typeof item === 'string' ? item === productHandle : item.handle === productHandle));

      if (productHandle && isActive) {
        btn.classList.add('active');
        const svg = btn.querySelector('svg');
        if (svg) {
          svg.setAttribute('fill', 'currentColor');
        }
      }
    });
    
    // Update wishlist count on load
    updateWishlistCount();
    
    // Refresh wishlist drawer on page load
    setTimeout(() => {
      refreshWishlistDrawer();
    }, 300);
  }

  async function refreshWishlistDrawer() {
    const container = $('#wishlistDrawerItems');
    const footer = $('#wishlistDrawerFooter');
    
    if (!container) {
      console.log('Wishlist drawer container not found');
      return;
    }
    
    let wishlist = JSON.parse(localStorage.getItem('theme_wishlist') || '[]');
    // Migration
    wishlist = wishlist.map(item => typeof item === 'string' ? { handle: item, variantId: null } : item);
    
    console.log('Refreshing wishlist drawer with items:', wishlist);
    
    if (wishlist.length === 0) {
      container.innerHTML = `
        <div style="text-align: center; padding: var(--space-4xl) var(--space-xl);">
          <p style="color: var(--color-text-light); margin-bottom: var(--space-xl);">Your wishlist is empty.</p>
          <a href="${window.routes?.collections_url || '/collections'}" class="btn btn--primary btn--sm">Continue Shopping</a>
        </div>
      `;
      if (footer) footer.style.display = 'none';
      return;
    }
    
    if (footer) footer.style.display = 'block';
    
    try {
      const fetchPromises = wishlist.map(item => {
        console.log('Fetching product:', item.handle);
        return fetch(`/products/${item.handle}.js`)
          .then(res => {
            return res.ok ? res.json() : null;
          })
          .then(product => {
            if (product) {
              product.savedVariantId = item.variantId;
            }
            return product;
          })
          .catch(err => {
            console.error('Error fetching product:', item.handle, err);
            return null;
          });
      });
      
      const products = await Promise.all(fetchPromises);
      const validProducts = products.filter(p => p !== null);
      
      if (validProducts.length === 0) {
        container.innerHTML = `
          <div style="text-align: center; padding: var(--space-4xl) var(--space-xl);">
            <p style="color: var(--color-text-light); margin-bottom: var(--space-xl);">Your wishlist is empty.</p>
            <a href="${window.routes?.collections_url || '/collections'}" class="btn btn--primary btn--sm">Continue Shopping</a>
          </div>
        `;
        if (footer) footer.style.display = 'none';
        return;
      }
      
      container.innerHTML = '';
      
      validProducts.forEach(product => {
        const variantId = product.savedVariantId;
        const variant = product.variants.find(v => v.id == variantId) || product.variants[0];
        const wishlistItem = document.createElement('div');
        wishlistItem.className = 'wishlist-item';
        
        let priceHTML = formatMoney(variant.price);
        
        // Get the best available image
        let imageUrl = variant.featured_image ? (variant.featured_image.src || variant.featured_image) : null;
        if (!imageUrl && product.featured_image) {
          imageUrl = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src;
        }
        if (!imageUrl && product.images && product.images.length > 0) {
          imageUrl = typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
        }
        
        let imageHTML = imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy">` : `<div class="wishlist-placeholder">No Image</div>`;
        
        const variantTitle = variant.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : '';
        
        wishlistItem.innerHTML = `
          <a href="/products/${product.handle}?variant=${variant.id}" class="wishlist-item__image">
            ${imageHTML}
          </a>
          <div class="wishlist-item__details">
            <a href="/products/${product.handle}?variant=${variant.id}" class="wishlist-item__title">${product.title}${variantTitle}</a>
            <div class="wishlist-item__price">${priceHTML}</div>
            <div class="wishlist-item__actions">
              <button class="wishlist-item__add-to-cart btn btn--sm" data-handle="${product.handle}" data-variant-id="${variant.id}" style="padding: 4px 8px; font-size: 10px; min-height: auto;">Add to Cart</button>
              <button class="wishlist-item__remove" data-handle="${product.handle}" data-variant-id="${variant.id}" style="font-size: 10px; background: none; border: none; color: #999; cursor: pointer;">Remove</button>
            </div>
          </div>
        `;
        container.appendChild(wishlistItem);
      });
      
    } catch (error) {
      console.error('Error refreshing wishlist:', error);
    }
  }

  async function updateWishlistCount() {
    const wishlist = JSON.parse(localStorage.getItem('theme_wishlist') || '[]');
    const counts = $$('.wishlist-count');
    counts.forEach(el => {
      el.textContent = wishlist.length;
      el.style.display = wishlist.length > 0 ? 'flex' : 'none';
    });
  }

  // Event listener for wishlist item actions (add to cart, remove)
  document.addEventListener('click', async function(e) {
    // Remove from wishlist
    const removeBtn = e.target.closest('.wishlist-item__remove');
    if (removeBtn) {
      e.preventDefault();
      const handle = removeBtn.dataset.handle;
      const removedVariantId = removeBtn.dataset.variantId;
      if (!handle) return;
      
      let wishlist = JSON.parse(localStorage.getItem('theme_wishlist') || '[]');
      // Migration/Safety
      wishlist = wishlist.map(item => typeof item === 'string' ? { handle: item, variantId: null } : item);

      const index = wishlist.findIndex(item => item.handle === handle && (removedVariantId ? item.variantId == removedVariantId : true));
      
      if (index > -1) {
        wishlist.splice(index, 1);
        localStorage.setItem('theme_wishlist', JSON.stringify(wishlist));
        localStorage.setItem('adorn_wishlist', JSON.stringify(wishlist.map(i => i.handle)));
        
        // Update UI
        const item = removeBtn.closest('.wishlist-item');
        if (item) {
          item.style.opacity = '0';
          item.style.transition = 'opacity 0.3s ease';
          setTimeout(() => {
            item.remove();
            refreshWishlistDrawer();
            updateWishlistCount();
            
            // Also update product card wishlist buttons if they exist
            $$('.product-card__wishlist, .product-info__wishlist-btn').forEach(btn => {
              if (btn.dataset.productHandle === handle) {
                const isActive = wishlist.some(i => i.handle === handle);
                if (!isActive) {
                  btn.classList.remove('active');
                  const svg = btn.querySelector('svg');
                  if (svg) svg.setAttribute('fill', 'none');
                }
              }
            });
          }, 300);
        }
      }
      return;
    }
    
    // Add to cart from wishlist — open Quick Add popup for variant selection
    const addToCartBtn = e.target.closest('.wishlist-item__add-to-cart');
    if (addToCartBtn) {
      e.preventDefault();
      const handle = addToCartBtn.dataset.handle;
      if (!handle) return;

      const originalText = addToCartBtn.textContent;
      addToCartBtn.textContent = 'Loading...';
      addToCartBtn.disabled = true;

      try {
        const res = await fetch('/products/' + handle + '.js');
        if (!res.ok) throw new Error('Product fetch failed');
        const product = await res.json();

        const hasRealVariants = product.options && product.options.length > 0 &&
          !(product.options.length === 1 && product.options[0].name === 'Title' && product.variants.length === 1);

        if (hasRealVariants) {
          // Close wishlist drawer
          const wishlistDrawer = $('.wishlist-drawer');
          const wishlistOverlay = $('.wishlist-drawer-overlay');
          if (wishlistDrawer) drawerManager.close(wishlistDrawer, wishlistOverlay);

          // Open the Quick Add popup
          const popup = document.getElementById('quickAddPopup');
          if (popup) {
            const virtualBtn = document.createElement('button');
            virtualBtn.className = 'js-quick-add-btn';
            virtualBtn.dataset.productHandle = handle;
            virtualBtn.dataset.productTitle = product.title;
            virtualBtn.dataset.productPrice = formatMoney(product.variants[0].price);
            const imgUrl = product.featured_image || (product.images && product.images.length > 0 ? product.images[0] : '');
            if (imgUrl) virtualBtn.dataset.productImage = typeof imgUrl === 'string' ? imgUrl : imgUrl.src;
            virtualBtn.style.display = 'none';
            document.body.appendChild(virtualBtn);
            virtualBtn.click();
            setTimeout(() => virtualBtn.remove(), 100);
          }
          addToCartBtn.textContent = originalText;
          addToCartBtn.disabled = false;
        } else {
          // Single variant — add directly
          const variantId = product.variants[0].id;
          addToCartBtn.textContent = 'Adding...';

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });

          if (addRes.ok) {
            addToCartBtn.textContent = 'Added!';
            updateCartCount();
            await refreshCartDrawer();

            let wishlist = JSON.parse(localStorage.getItem('theme_wishlist') || '[]');
            wishlist = wishlist.map(item => typeof item === 'string' ? { handle: item, variantId: null } : item);
            const idx = wishlist.findIndex(item => item.handle === handle);
            if (idx > -1) {
              wishlist.splice(idx, 1);
              localStorage.setItem('theme_wishlist', JSON.stringify(wishlist));
              localStorage.setItem('adorn_wishlist', JSON.stringify(wishlist.map(i => i.handle)));
              updateWishlistCount();
            }
            setTimeout(() => refreshWishlistDrawer(), 500);

            const cartDrawer = $('.cart-drawer');
            const cartOverlay = $('.cart-drawer-overlay');
            if (cartDrawer) drawerManager.open(cartDrawer, cartOverlay);

            setTimeout(() => {
              addToCartBtn.textContent = originalText;
              addToCartBtn.disabled = false;
            }, 1000);
          } else {
            throw new Error('Failed to add to cart');
          }
        }
      } catch (error) {
        console.error('Error adding to cart from wishlist:', error);
        addToCartBtn.textContent = 'Error';
        setTimeout(() => {
          addToCartBtn.textContent = originalText;
          addToCartBtn.disabled = false;
        }, 2000);
      }
    }
  });

  /* ---- Hero Slider ---- */
  function initHeroSlider() {
    const section = $('.hero-banner');
    if (!section) return;

    const track = section.querySelector('.hero-banner__track');
    const slides = section.querySelectorAll('.hero-banner__slide');
    const dots = section.querySelectorAll('.hero-banner__dot');
    
    if (!track || slides.length <= 1) return;

    let currentIndex = 0;
    let autoplayInterval;
    const autoplay = section.dataset.autoplay === 'true';
    const speed = parseInt(section.dataset.speed) || 5000;

    function goToSlide(index) {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
      
      currentIndex = index;
      track.style.transform = `translateX(-${currentIndex * 100}%)`;
      
      // Update dots
      dots.forEach((dot, i) => {
        dot.classList.toggle('hero-banner__dot--active', i === currentIndex);
      });
      
      // Trigger typing animation for active slide
      triggerSlideTypingAnimation(slides[currentIndex]);
    }
    
    function triggerSlideTypingAnimation(slide) {
      const typingElements = slide.querySelectorAll('[data-typing-text]');
      typingElements.forEach((el, index) => {
        const text = el.textContent;
        el.textContent = '';
        el.classList.remove('typing-active');
        
        let charIndex = 0;
        const typingSpeed = 40;
        
        function typeCharacter() {
          if (charIndex < text.length) {
            el.textContent += text.charAt(charIndex);
            charIndex++;
            setTimeout(typeCharacter, typingSpeed);
          } else {
            el.classList.add('typing-active');
          }
        }
        
        setTimeout(typeCharacter, 300 + (index * 200));
      });
    }

    function startAutoplay() {
      if (!autoplay) return;
      stopAutoplay();
      autoplayInterval = setInterval(() => {
        goToSlide(currentIndex + 1);
      }, speed);
    }

    function stopAutoplay() {
      if (autoplayInterval) clearInterval(autoplayInterval);
    }

    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index);
        goToSlide(index);
        stopAutoplay();
        startAutoplay(); // Reset timer
      });
    });

    // Pause on hover
    section.addEventListener('mouseenter', stopAutoplay);
    section.addEventListener('mouseleave', startAutoplay);

    // Trigger typing for first slide
    triggerSlideTypingAnimation(slides[0]);
    
    // Initial start
    startAutoplay();
  }

  /* ---- Typing Animation ---- */
  function initTypingAnimation() {
    const typingElements = document.querySelectorAll('[data-typing-text]');
      
    typingElements.forEach((el, index) => {
      const text = el.textContent;
      el.textContent = '';
      el.style.opacity = '1';
        
      let charIndex = 0;
      const typingSpeed = 40;
        
      function typeCharacter() {
        if (charIndex < text.length) {
          el.textContent += text.charAt(charIndex);
          charIndex++;
          setTimeout(typeCharacter, typingSpeed);
        } else {
          el.classList.add('typing-active');
        }
      }
        
      // Start typing with delay based on element position
      setTimeout(typeCharacter, 300 + (index * 200));
    });
  }
  
  /* ---- Page Transitions ---- */
  function initPageTransitions() {
    // Create transition overlay
    const overlay = document.createElement('div');
    overlay.className = 'page-transition-overlay';
    document.body.appendChild(overlay);
      
    // Handle internal link clicks
    document.addEventListener('click', function(e) {
      const link = e.target.closest('a');
      if (!link) return;
        
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
      if (link.target === '_blank') return;
      if (link.classList.contains('no-transition')) return;
        
      // Only apply to internal links
      const isInternal = href.startsWith('/') || href.includes(window.location.hostname);
      if (!isInternal) return;
        
      e.preventDefault();
        
      // Activate transition
      overlay.classList.add('active');
      document.querySelector('.main-content')?.classList.add('page-loading');
        
      // Navigate after animation
      setTimeout(() => {
        window.location.href = href;
      }, 300);
    });
      
    // Remove loading state on page load
    window.addEventListener('load', function() {
      overlay.classList.remove('active');
      document.querySelector('.main-content')?.classList.remove('page-loading');
    });
  }
  
  /* ---- Fix Blank Screen on Back Navigation (bfcache) ---- */
  function initBfcacheFix() {
    // Listen for pageshow event (fires when page is shown, including from bfcache)
    window.addEventListener('pageshow', function(event) {
      // If page is loaded from bfcache (persisted property is true)
      if (event.persisted) {
        console.log('Page restored from bfcache - reloading...');
        // Force a fresh reload to avoid blank screen
        window.location.reload();
      }
    });
    
    // Alternative: Listen for pagehide to prepare for bfcache
    window.addEventListener('pagehide', function() {
      // Clear any potentially problematic state before caching
      console.log('Page being cached - cleaning up...');
    });
  }
  

  /* ---- Quick Add Popup ---- */
  function initQuickAddPopup() {
    var popup = document.getElementById('quickAddPopup');
    if (!popup) return;

    var closeBtn = document.getElementById('quickAddClose');
    var backdrop = document.getElementById('quickAddBackdrop');
    var form = document.getElementById('quickAddForm');
    var optsWrap = document.getElementById('quickAddOptions');
    var variantInput = document.getElementById('quickAddVariantId');
    var submitBtn = document.getElementById('quickAddSubmitBtn');
    var titleEl = document.getElementById('quickAddTitle');
    var priceEl = document.getElementById('quickAddPrice');
    var imgEl = document.getElementById('quickAddImage');

    var prodData = null;
    var selected = {};

    function openPopup() {
      popup.style.display = 'flex';
      requestAnimationFrame(function() {
        requestAnimationFrame(function() { popup.classList.add('is-open'); });
      });
      document.body.style.overflow = 'hidden';
    }

    function closePopup() {
      popup.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(function() {
        popup.style.display = 'none';
        optsWrap.innerHTML = '';
        prodData = null;
        selected = {};
      }, 350);
    }

    function escHtml(s) {
      var d = document.createElement('div');
      d.textContent = s || '';
      return d.innerHTML;
    }

    function renderOptions(product) {
      if (!product || !product.options || product.options.length === 0 ||
          (product.options.length === 1 && product.options[0].name === 'Title')) {
        optsWrap.innerHTML = '';
        if (product.variants && product.variants.length > 0) {
          variantInput.value = product.variants[0].id;
          submitBtn.disabled = !product.variants[0].available;
          submitBtn.textContent = product.variants[0].available ? 'Add to Cart' : 'Sold Out';
        }
        return;
      }

      selected = {};
      var html = '';
      product.options.forEach(function(opt, idx) {
        selected['option' + (idx+1)] = opt.values[0];
        html += '<div class="qa-opt"><span class="qa-opt__label">' + escHtml(opt.name) + '</span><div class="qa-opt__values">';
        opt.values.forEach(function(val) {
          var active = (selected['option' + (idx+1)] === val) ? ' is-active' : '';
          html += '<button type="button" class="qa-opt__btn js-qa-opt' + active + '" data-idx="option' + (idx+1) + '" data-val="' + escHtml(val) + '">' + escHtml(val) + '</button>';
        });
        html += '</div></div>';
      });
      optsWrap.innerHTML = html;
      matchVariant();
    }

    function matchVariant() {
      if (!prodData) return;
      var match = prodData.variants.find(function(v) {
        return (!selected.option1 || v.option1 === selected.option1) &&
               (!selected.option2 || v.option2 === selected.option2) &&
               (!selected.option3 || v.option3 === selected.option3);
      });
      if (match) {
        variantInput.value = match.id;
        priceEl.textContent = formatMoney(match.price);
        if (match.featured_image && match.featured_image.src) {
          imgEl.src = match.featured_image.src;
        }
        submitBtn.disabled = !match.available;
        submitBtn.textContent = match.available ? 'Add to Cart' : 'Sold Out';
      } else {
        variantInput.value = '';
        submitBtn.disabled = true;
        submitBtn.textContent = 'Unavailable';
      }
    }

    // Delegated click on quick add buttons
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.js-quick-add-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var handle = btn.dataset.productHandle;
      if (!handle) return;

      titleEl.textContent = btn.dataset.productTitle || '';
      priceEl.textContent = btn.dataset.productPrice || '';
      if (btn.dataset.productImage) imgEl.src = btn.dataset.productImage;

      optsWrap.innerHTML = '<div class="qa-popup__loading">Loading options...</div>';
      submitBtn.disabled = true;
      openPopup();

      fetch('/products/' + handle + '.js')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          prodData = data;
          renderOptions(data);
        })
        .catch(function() {
          optsWrap.innerHTML = '<div class="qa-popup__loading" style="color:#c00">Failed to load. Please try again.</div>';
        });
    });

    // Option selection
    optsWrap.addEventListener('click', function(e) {
      var btn = e.target.closest('.js-qa-opt');
      if (!btn) return;
      selected[btn.dataset.idx] = btn.dataset.val;
      var siblings = btn.parentElement.querySelectorAll('.js-qa-opt');
      siblings.forEach(function(b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');
      matchVariant();
    });

    // Submit
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (!variantInput.value) return;
      submitBtn.disabled = true;
      var origText = submitBtn.textContent;
      submitBtn.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantInput.value, quantity: 1 })
      })
      .then(function(r) {
        if (!r.ok) throw new Error('fail');
        return r.json();
      })
      .then(function(data) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        closePopup();
        refreshCartDrawer();
        updateCartCount();
        var drawer2 = document.querySelector('.cart-drawer');
        var overlay2 = document.querySelector('.cart-drawer-overlay');
        if (drawer2) drawerManager.open(drawer2, overlay2);
      })
      .catch(function() {
        submitBtn.textContent = 'Error — Retry';
        setTimeout(function() { submitBtn.textContent = origText; submitBtn.disabled = false; }, 2000);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closePopup);
    if (backdrop) backdrop.addEventListener('click', closePopup);
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) closePopup();
    });
  }

  /* ---- Initialize Everything ---- */
  function init() {
    initHeader();
    initMobileMenu();
    initAnnouncementBar();
    initSearch();
    initCartDrawer();
    initAddToCart();
    initQuickAddForm();
    initQuickAddPopup();
    initQuickView();
    initProductPage();
    initFAQ();
    initScrollAnimations();
    initLazyLoad();
    initCollectionPage();
    initNewsletter();
    initCartPage();
    initParallax();
    initCookieConsent();
    initWishlist();
    initHeroSlider();
    initTypingAnimation();
    initPageTransitions();
    initBfcacheFix();
    updateCartCount();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section load/unload (Theme editor support)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
})();
